import { Injectable, inject } from '@angular/core';
import { MapService } from './map.service';
import { LocationService } from '../platform/location.service';

export interface PlaceSearchOptions {
  /** Bias towards the user's position instead of the visible map area ("nearest ..."). */
  preferClosest?: boolean;
}

export interface ResolvedPlace {
  name: string;
  address: string;
  lat: number;
  lon: number;
}

/**
 * Resolves a free-text place ("Szentendre", "Andrássy út 10, Budapest") to one coordinate without
 * any UI, using the same two Places calls as the search box in SearchComponent: one autocomplete
 * request (top suggestion), then the suggestion's location. Billed as Autocomplete Requests +
 * Place Details Essentials, a fraction of a Text Search with the same result fields.
 */
@Injectable({ providedIn: 'root' })
export class PlaceSearchService {
  private static readonly CLOSEST_SEARCH_RADIUS_M: number = 10000;

  private readonly mapService = inject(MapService);
  private readonly locationService = inject(LocationService);

  /** Returns the best match, or null when nothing was found or the Places library is unavailable. */
  public async findPlace(query: string, options: PlaceSearchOptions = {}): Promise<ResolvedPlace | null> {
    if (!query?.trim() || typeof google === 'undefined' || !google.maps?.places?.AutocompleteSuggestion) {
      return null;
    }

    const request: google.maps.places.AutocompleteRequest = {
      input: query.trim(),
      sessionToken: new google.maps.places.AutocompleteSessionToken()
    };

    const position = this.locationService.getLastPosition();
    if (options.preferClosest && position) {
      const center = { lat: position.Latitude, lng: position.Longitude };
      request.locationBias = { center, radius: PlaceSearchService.CLOSEST_SEARCH_RADIUS_M };
      request.origin = center;
    } else {
      // Same bias as the search box: prefer what is on screen, but still find places elsewhere.
      request.locationBias = this.mapService.getMapBounds();
    }

    try {
      const { suggestions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
      const prediction = suggestions.find(s => s.placePrediction)?.placePrediction;
      if (!prediction) {
        return null;
      }

      // toPlace() keeps the session token, so this details call closes the autocomplete session.
      const place = prediction.toPlace();
      await place.fetchFields({ fields: ['location'] });
      if (!place.location) {
        return null;
      }

      return {
        name: prediction.text?.text || query,
        address: prediction.secondaryText?.text || '',
        lat: place.location.lat(),
        lon: place.location.lng()
      };
    } catch (error) {
      console.error('Place search failed:', error);
      return null;
    }
  }
}
